import { useState, useEffect, useCallback } from 'preact/hooks'
import { Loader2 } from 'lucide-preact'
import { useAuth } from '@/auth/useAuth'
import { useWebLoader } from '@/hooks/useWebLoader'
import { useLandingPageChat } from '@/hooks/useLandingPageChat'
import { landingPagesApi } from '@/api/landingPages'
import type { LandingPageLayout, LandingPageGenerateRequestDto, LandingPageContextDto, LandingPageViewDto, ThankYouPageSettingsDto, PageMetaDto, LandingPageContentImageDto } from '@/types/landingPage'
import type { WebsterAttachment } from '@/types/webster'

import { LandingPageSidebar } from '@/components/webs/landingPage/LandingPageSidebar'
import { detectFontDirective } from '@/utils/fontDirective'
import { LandingPageEditorPanel } from '@/components/webs/landingPage/LandingPageEditorPanel'
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview'
import { WebsterChatInput } from '@/components/webster/WebsterChatInput'
import { ExternalModeNotice } from '@/components/webs/overlays/WebsterBuilderShared'

export interface LandingPageBuilderProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    assetId: string
    onPromoteDraft: (options?: { versionComment?: string }) => Promise<boolean>
    promoting: boolean
    onReadyForVersionChange?: (ready: boolean) => void
    /** Fires when the LP entity becomes available (post-generation). Lets the
     *  parent re-fetch the WebAsset so its LandingPageId / status are fresh. */
    onLandingPageEstablished?: () => void
    /** Fires after each chat-driven rebuild completes. Same parent action as
     *  onLandingPageEstablished — re-fetch the WebAsset — but on every edit
     *  so DraftUpdatedAt stays current and the Save-as-Version button can
     *  re-enable when the draft moves ahead of the latest version. Return
     *  value is ignored; if a Promise is returned it is awaited. */
    onLandingPageRebuilt?: () => unknown
    /** Reports the LP entity's DraftUpdatedAt to the parent. For LPs this is
     *  the authoritative draft timestamp (WebAsset.DraftUpdatedAt is not
     *  maintained server-side for LPs — the LP entity owns it). */
    onDraftUpdatedAtChange?: (value: string | null) => void
    /** Reports the generating state so the parent can disable Save-as-Version while
     *  Webster is working. For LPs the chat hook's `generating` flag already spans both
     *  the initial generation and chat-driven rebuilds, so one flag covers both. */
    onGeneratingChange?: (value: boolean) => void
}

export function LandingPageBuilder({
    clientCompanyId,
    workspaceId,
    campaignId,
    webId,
    assetId,
    onReadyForVersionChange,
    onLandingPageEstablished,
    onLandingPageRebuilt,
    onDraftUpdatedAtChange,
    onGeneratingChange,
}: LandingPageBuilderProps) {
    const { web } = useWebLoader(workspaceId, webId)
    const { userProfile } = useAuth()

    const {
        messages, generating, hasGenerated,
        landingPage, setLandingPage, html, loading,
        context, contextLoading,
        thankYouHtml, thankYouGenerating, loadThankYouDraft, updateThankYouSettings,
        regenerateThankYou, suggestThankYouGeneration, canGenerateThankYou,
        chatReady,
        generationError, thankYouGenerationError,
        startGeneration, sendMessage, retryGeneration, loadPreview,
        updateSettings, updateContext, updatePageMeta, applyFont, applyFontFromChat,
        turnState, turnUserId, turnUserName,
        designQuestions, designRound, submittingAnswers, submitDesignAnswers,
    } = useLandingPageChat({ clientCompanyId, workspaceId, campaignId, webId, onRebuilt: onLandingPageRebuilt })

    // Multi-user turn lock. When another user is mid-turn on the shared chat
    // session, lock this user's input and attribute the wait. Falls through to
    // null when the turn is idle or this user is the holder (their own input is
    // already disabled by `generating`).
    // Fallback to a generic label if turnUserName is missing — without it the
    // input wouldn't lock at all (lockedByUserName goes null), defeating the
    // turn-lock UX when the holder's name happens to be empty on the server side.
    const lockedByUserName = (turnState === 'Active' && turnUserId && turnUserId !== userProfile?.Id)
        ? (turnUserName || 'Another user')
        : null

    // Local settings state
    const [selectedLayout, setSelectedLayout] = useState<LandingPageLayout>(
        landingPage?.Settings?.Layout || 'Hero Flow'
    )
    const [showAgenda, setShowAgenda] = useState(landingPage?.Settings?.ShowAgenda ?? true)
    const [showSpeakerBios, setShowSpeakerBios] = useState(landingPage?.Settings?.ShowSpeakerBios ?? true)
    const [showCountdown, setShowCountdown] = useState(landingPage?.Settings?.ShowCountdown ?? true)
    const [showDuration, setShowDuration] = useState(landingPage?.Settings?.ShowDuration ?? true)
    // Always defaults true — independent of company-level TrackingEnabled (that
    // gate is enforced server-side at event ingestion, not at LP generation).
    const [includeTracking, setIncludeTracking] = useState(landingPage?.Settings?.IncludeTracking ?? true)
    // Per-LP opt-in (default false). Set in the setup sidebar, carried in the generate
    // request, and baked into the first version at generation — same as Layout. Read-only
    // in the post-gen edit panel; changed afterward via the Settings PATCH + a regenerate.
    const [includeCustomScript, setIncludeCustomScript] = useState(landingPage?.Settings?.IncludeCustomScript ?? false)
    const [formFields, setFormFields] = useState<string[] | null>(landingPage?.Settings?.FormFields ?? null)
    // Per-page font override (Secondary = heading, Primary = body).
    const [fontHeading, setFontHeading] = useState<string | undefined>(landingPage?.Settings?.FontOverrideSecondary)
    const [fontBody, setFontBody] = useState<string | undefined>(landingPage?.Settings?.FontOverridePrimary)

    const [showPreview, setShowPreview] = useState(!!landingPage)
    useEffect(() => {
        if (generating || hasGenerated) setShowPreview(true)
    }, [generating, hasGenerated])

    const [localContext, setLocalContext] = useState<LandingPageContextDto | null>(null)
    const [localThankYouSettings, setLocalThankYouSettings] = useState<ThankYouPageSettingsDto | null>(null)
    const [localPageMeta, setLocalPageMeta] = useState<PageMetaDto | null>(null)
    const [localContentImages, setLocalContentImages] = useState<LandingPageContentImageDto[]>([])

    useEffect(() => { if (context) setLocalContext(context) }, [context])
    useEffect(() => { if (landingPage?.PageMeta) setLocalPageMeta(landingPage.PageMeta) }, [landingPage?.PageMeta])
    useEffect(() => { if (landingPage?.ContentImages) setLocalContentImages(landingPage.ContentImages) }, [landingPage?.ContentImages])
    useEffect(() => {
        if (landingPage?.ThankYouSettings) setLocalThankYouSettings(landingPage.ThankYouSettings)
    }, [landingPage?.ThankYouSettings])

    useEffect(() => {
        if (landingPage?.Settings) {
            setSelectedLayout(landingPage.Settings.Layout)
            setShowAgenda(landingPage.Settings.ShowAgenda)
            setShowSpeakerBios(landingPage.Settings.ShowSpeakerBios)
            setShowCountdown(landingPage.Settings.ShowCountdown)
            setShowDuration(landingPage.Settings.ShowDuration)
            // Defensive ?? true for pre-feature LPs whose Settings doc
            // pre-dates the IncludeTracking field.
            setIncludeTracking(landingPage.Settings.IncludeTracking ?? true)
            setIncludeCustomScript(landingPage.Settings.IncludeCustomScript ?? false)
            setFormFields(landingPage.Settings.FormFields ?? null)
            setFontHeading(landingPage.Settings.FontOverrideSecondary)
            setFontBody(landingPage.Settings.FontOverridePrimary)
        }
    }, [landingPage?.Id])

    useEffect(() => {
        if (web && !((web.Type || '').toLowerCase().includes('online') || !!web.OnlineEvent) && !landingPage?.Settings) {
            setShowAgenda(false)
            setShowSpeakerBios(false)
            setShowCountdown(false)
            setShowDuration(false)
        }
    }, [web, landingPage?.Settings])

    useEffect(() => {
        if (hasGenerated && !html && !generating) loadPreview()
    }, [hasGenerated, html, generating, loadPreview])

    // Report to parent whether LP is ready for "Save as Version"
    // Ready = has been generated AND thank you page is done (canGenerateThankYou === false)
    useEffect(() => {
        onReadyForVersionChange?.(hasGenerated && !canGenerateThankYou)
    }, [hasGenerated, canGenerateThankYou, onReadyForVersionChange])

    // Push LP-entity DraftUpdatedAt up to the parent — it owns the Save gate
    // and for LPs this is the authoritative draft timestamp.
    useEffect(() => {
        onDraftUpdatedAtChange?.(landingPage?.DraftUpdatedAt ?? null)
    }, [landingPage?.DraftUpdatedAt, onDraftUpdatedAtChange])

    // Push generating up so the Save-as-Version button can be disabled while Webster is
    // working. For LPs `generating` already spans the initial generation and chat rebuilds.
    useEffect(() => {
        onGeneratingChange?.(generating)
    }, [generating, onGeneratingChange])

    // Notify parent when the LP entity first becomes available so it can
    // re-fetch the WebAsset (its LandingPageId is denormalized on the asset
    // and is what gates the "Save as Version" button).
    useEffect(() => {
        if (landingPage?.Id) onLandingPageEstablished?.()
    }, [landingPage?.Id, onLandingPageEstablished])

    useEffect(() => {
        if (hasGenerated && !thankYouHtml && !generating && !thankYouGenerating && landingPage?.ThankYouGenerationStatus === 'Completed') {
            loadThankYouDraft()
        }
    }, [hasGenerated, thankYouHtml, generating, thankYouGenerating, loadThankYouDraft, landingPage?.ThankYouGenerationStatus])

    const handleSend = async (message: string, attachments?: WebsterAttachment[]) => {
        if (!hasGenerated) {
            // Flip to the rocket/preview surface immediately so the user gets visual
            // feedback the moment they click Submit. The useEffect below also sets this
            // once chat.generating propagates, but doing it synchronously here means we
            // don't depend on state-update timing or extension-init races inside the
            // RichTextBoxes still mounted in the setup sidebar.
            setShowPreview(true)
            const request: LandingPageGenerateRequestDto = {
                Layout: selectedLayout,
                ShowAgenda: showAgenda,
                ShowSpeakerBios: showSpeakerBios,
                ShowCountdown: showCountdown,
                ShowDuration: showDuration,
                IncludeTracking: includeTracking,
                IncludeCustomScript: includeCustomScript,
                FontOverrideSecondary: fontHeading,
                FontOverridePrimary: fontBody,
                Context: localContext ?? undefined,
                ThankYouSettings: localThankYouSettings ?? undefined,
                PageMeta: localPageMeta ?? undefined,
                ContentImages: localContentImages.length > 0 ? localContentImages : undefined,
                AssetId: assetId,
            }
            await startGeneration(request, message || undefined)
        } else if (message || (attachments && attachments.length > 0)) {
            // A clear, text-only font directive ("use Poppins") applies instantly via the
            // apply-font endpoint instead of spending an AI rebuild; everything else is
            // normal Webster chat.
            const fontDirective = message && !(attachments && attachments.length > 0)
                ? detectFontDirective(message)
                : null
            if (fontDirective) {
                setFontHeading(fontDirective.heading ?? undefined)
                setFontBody(fontDirective.body ?? undefined)
                await applyFontFromChat(message, fontDirective.heading, fontDirective.body)
                return
            }
            await sendMessage(message, attachments)
        }
    }

    const handleToggleSetting = (field: 'ShowAgenda' | 'ShowSpeakerBios' | 'ShowCountdown' | 'ShowDuration' | 'IncludeTracking' | 'IncludeCustomScript', value: boolean) => {
        switch (field) {
            case 'ShowAgenda': setShowAgenda(value); break
            case 'ShowSpeakerBios': setShowSpeakerBios(value); break
            case 'ShowCountdown': setShowCountdown(value); break
            case 'ShowDuration': setShowDuration(value); break
            case 'IncludeTracking': setIncludeTracking(value); break
            case 'IncludeCustomScript': setIncludeCustomScript(value); break
        }
        if (landingPage) updateSettings({ ETag: landingPage.ETag, [field]: value })
    }

    const handleLayoutChange = (layout: LandingPageLayout) => {
        setSelectedLayout(layout)
        if (landingPage) updateSettings({ ETag: landingPage.ETag, Layout: layout })
    }

    const handleFormFieldsChange = (canonicalKeys: string[]) => {
        setFormFields(canonicalKeys)
        if (landingPage) updateSettings({ ETag: landingPage.ETag, FormFields: canonicalKeys })
    }

    // Effective fonts shown in the picker: the per-page override wins, else the
    // company's scraped brand font (snapshotted onto the context).
    const effectiveHeadingFont = fontHeading ?? localContext?.BrandFontSecondary ?? undefined
    const effectiveBodyFont = fontBody ?? localContext?.BrandFontPrimary ?? undefined

    const handleApplyFont = useCallback(async (heading: string | null, body: string | null) => {
        // Only pin the role the user actually changed. If an incoming value equals the
        // current effective font (that role wasn't touched), keep its existing state — an
        // explicit override, or inheriting the company brand font — rather than freezing
        // the brand default into an override.
        const nextHeading = heading === (effectiveHeadingFont ?? null) ? (fontHeading ?? null) : heading
        const nextBody = body === (effectiveBodyFont ?? null) ? (fontBody ?? null) : body
        setFontHeading(nextHeading ?? undefined)
        setFontBody(nextBody ?? undefined)
        // Post-generation: re-bake the draft instantly. Pre-generation: the override
        // rides into the Generate request body (handleSend) instead.
        if (landingPage) await applyFont(nextHeading, nextBody)
    }, [landingPage, applyFont, effectiveHeadingFont, effectiveBodyFont, fontHeading, fontBody])

    // Post-generation only: ask Webster to tune type scale/spacing for the current fonts
    // (a targeted edit via the normal chat flow — preserves copy/layout/colors).
    const handleRefineFont = useCallback(() => {
        void sendMessage('Refine the typography to suit the current fonts — adjust the type scale, line-height, letter-spacing, and font weights for balance and readability. Do not change any copy, layout, colors, or images.')
    }, [sendMessage])

    const handleContextChange = useCallback((field: keyof LandingPageContextDto, value: string | string[]) => {
        setLocalContext(prev => prev ? { ...prev, [field]: value } : { [field]: value })
        // Persist post-generation edits so a refresh doesn't lose the user's input.
        // Pre-generation, the value flows through the Generate request body so
        // there's nothing to save until landingPage exists.
        if (landingPage) updateContext({ ETag: landingPage.ETag, [field]: value })
    }, [landingPage, updateContext])

    const handleThankYouSettingsChange = useCallback((field: keyof ThankYouPageSettingsDto, value: string) => {
        setLocalThankYouSettings(prev => prev ? { ...prev, [field]: value } : { [field]: value } as ThankYouPageSettingsDto)
        if (landingPage) updateThankYouSettings({ ETag: landingPage.ETag, [field]: value })
    }, [landingPage, updateThankYouSettings])

    const handlePageMetaChange = useCallback((next: PageMetaDto) => {
        // Update local state synchronously so the controlled inputs in
        // PageMetaSection don't visibly revert while the PATCH is in flight.
        setLocalPageMeta(next)
        // Post-generation: persist immediately. Pre-generation: localPageMeta
        // is carried into the Generate request body so the values land on the
        // freshly-created LandingPage entity.
        if (landingPage) {
            updatePageMeta({
                ETag: landingPage.ETag,
                OgImageAlt: next.OgImageAlt,
                MetaDescription: next.MetaDescription,
            })
        }
    }, [landingPage, updatePageMeta])

    const handleLandingPageUpdate = useCallback((updated: LandingPageViewDto) => {
        setLandingPage(updated)
    }, [setLandingPage])

    // Content images (ORB-413). Pre-generation, edits ride into the Generate request
    // body via localContentImages (images persist server-side at upload time; note
    // edits stay local until generation carries them into the manifest). Post-gen the
    // section is read-only/locked, so this only fires from the setup sidebar.
    const handleContentImagesChange = useCallback((next: LandingPageContentImageDto[]) => {
        setLocalContentImages(next)
    }, [])

    // Composer paperclip upload — stores the image and returns its URL for the
    // pending-attachment strip. Threaded into the post-gen editor panel's chat.
    const handleUploadImage = useCallback(async (file: File) => {
        const res = await landingPagesApi.uploadContentImage(workspaceId, webId, file)
        if (!res.data) throw new Error(res.error || 'Failed to upload image')
        // Post-generation the endpoint appends the image to the LP and returns the updated
        // entity — adopt it so the read-only Page Images list reflects the new chat-uploaded
        // image without a reload (the ContentImages effect cascades to localContentImages).
        if (res.data.LandingPage) setLandingPage(res.data.LandingPage)
        return {
            url: res.data.ContentImage.Url,
            fileName: res.data.ContentImage.FileName,
            contentType: res.data.ContentImage.ContentType,
        }
    }, [workspaceId, webId, setLandingPage])

    // Wait for all critical data before showing the builder.
    // For existing LPs (hasGenerated), wait for HTML to load too so we don't
    // flash empty states. For new LPs (no generation yet), just need metadata + web.
    const isInitializing = loading || !web || (hasGenerated && !html && !generating)

    if (isInitializing) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">Loading...</span>
                </div>
            </div>
        )
    }

    if (landingPage?.Mode === 'External') {
        return <ExternalModeNotice url={landingPage.DeployedUrl} />
    }

    return (
        <div className="h-full flex overflow-hidden">
            {!showPreview ? (
                <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-y-auto">
                        <div className="max-w-3xl mx-auto">
                            <LandingPageSidebar
                                context={localContext}
                                contextLoading={contextLoading}
                                selectedLayout={selectedLayout}
                                onSelectLayout={handleLayoutChange}
                                showAgenda={showAgenda}
                                showSpeakerBios={showSpeakerBios}
                                showCountdown={showCountdown}
                                showDuration={showDuration}
                                includeTracking={includeTracking}
                                includeCustomScript={includeCustomScript}
                                onToggleSetting={handleToggleSetting}
                                formFields={formFields}
                                onFormFieldsChange={handleFormFieldsChange}
                                onContextChange={handleContextChange}
                                thankYouSettings={localThankYouSettings}
                                onThankYouSettingsChange={handleThankYouSettingsChange}
                                pageMeta={localPageMeta}
                                onPageMetaChange={handlePageMetaChange}
                                onLandingPageUpdate={handleLandingPageUpdate}
                                workspaceId={workspaceId}
                                webId={webId}
                                headingFont={effectiveHeadingFont}
                                bodyFont={effectiveBodyFont}
                                onApplyFont={handleApplyFont}
                                onRefineFont={landingPage ? handleRefineFont : undefined}
                                contentImages={localContentImages}
                                onContentImagesChange={handleContentImagesChange}
                                disabled={generating}
                                webType={web.Type}
                            />
                        </div>
                    </div>

                    <WebsterChatInput
                        onSend={handleSend}
                        disabled={generating}
                        lockedByUserName={lockedByUserName}
                        buttonLabel="Generate"
                        allowEmpty
                        placeholder="Describe your ideal landing page, or just click Generate to get started..."
                    />
                </div>
            ) : (
                <>
                    <div className="flex-1 min-w-0">
                        <HtmlPreview
                            html={html}
                            thankYouHtml={thankYouHtml}
                            loading={generating && !html && !thankYouGenerating}
                            thankYouGenerating={thankYouGenerating}
                            generationError={generationError}
                            thankYouGenerationError={thankYouGenerationError}
                            onRetry={retryGeneration}
                            canGenerateThankYou={canGenerateThankYou}
                            onGenerateThankYou={() => regenerateThankYou()}
                            // Outstanding questions freeze the bars on their own rather
                            // than waiting for the refetched status. The questions arriving
                            // IS the signal; making the freeze depend on a round-trip left
                            // the rocket visibly climbing while Webster was already asking.
                            awaitingDesignInput={
                                designQuestions.length > 0
                                || landingPage?.GenerationStatus === 'AwaitingInput'
                            }
                        />
                    </div>

                    <LandingPageEditorPanel
                        context={localContext}
                        contextLoading={contextLoading}
                        selectedLayout={selectedLayout}
                        showAgenda={showAgenda}
                        showSpeakerBios={showSpeakerBios}
                        showCountdown={showCountdown}
                        showDuration={showDuration}
                        includeTracking={includeTracking}
                        includeCustomScript={includeCustomScript}
                        onSelectLayout={handleLayoutChange}
                        onToggleSetting={handleToggleSetting}
                        formFields={formFields}
                        onFormFieldsChange={handleFormFieldsChange}
                        onContextChange={handleContextChange}
                        thankYouSettings={localThankYouSettings}
                        onThankYouSettingsChange={handleThankYouSettingsChange}
                        pageMeta={localPageMeta}
                        onPageMetaChange={handlePageMetaChange}
                        contentImages={localContentImages}
                        chat={{ messages, generating }}
                        designQuestions={designQuestions}
                        designRound={designRound}
                        submittingAnswers={submittingAnswers}
                        onSubmitDesignAnswers={(answers, note) => submitDesignAnswers(answers, note)}
                        onLetWebsterDecide={() => submitDesignAnswers([], null, true)}
                        onSend={handleSend}
                        onUploadImage={handleUploadImage}
                        disabled={generating}
                        chatDisabled={!chatReady}
                        lockedByUserName={lockedByUserName}
                        suggestThankYouGeneration={suggestThankYouGeneration}
                        onGenerateThankYou={() => regenerateThankYou()}
                        workspaceId={workspaceId}
                        webId={webId}
                        headingFont={effectiveHeadingFont}
                        bodyFont={effectiveBodyFont}
                        onApplyFont={handleApplyFont}
                        onRefineFont={landingPage ? handleRefineFont : undefined}
                        webType={web.Type}
                    />
                </>
            )}
        </div>
    )
}
